import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** 出错时的替代 UI;不传则用内置的友好兜底卡片。 */
  fallback?: ReactNode;
}
interface State {
  hasError: boolean;
  message?: string;
}

/**
 * 顶层渲染错误兜底 —— 任何子组件 render 抛错(如后端契约漂移导致的 undefined 访问)
 * 都被这里捕获成一张可恢复的卡片,而不是让整个应用白屏(用户第一铁律:永不崩溃)。
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: unknown): State {
    return { hasError: true, message: error instanceof Error ? error.message : String(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // 仅本地打点,不外发;方便开发时定位,生产环境也只落控制台。
    console.error("[ErrorBoundary] 捕获到渲染错误:", error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, message: undefined });
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;
    return (
      <div className="card stack" style={{ maxWidth: 520, margin: "48px auto", textAlign: "center" }}>
        <h2 style={{ margin: 0 }}>这个页面出了点问题</h2>
        <p style={{ fontSize: 13.5, color: "var(--ink-2)" }}>
          页面渲染时遇到意外错误,已被拦下,没有影响你的数据。可以重试,或返回工作台。
        </p>
        {this.state.message && (
          <p className="mono" style={{ fontSize: 12, color: "var(--ink-3)", wordBreak: "break-all" }}>
            {this.state.message}
          </p>
        )}
        <div className="cluster" style={{ justifyContent: "center" }}>
          <button type="button" className="btn btn-primary btn-sm" onClick={this.handleReset}>
            重试
          </button>
          <a href="/app" className="btn btn-secondary btn-sm">
            返回工作台
          </a>
        </div>
      </div>
    );
  }
}
